import axios from "axios";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router";
import api from "../../utils/api";
import { toast } from "react-toastify";

function SalaryStructure() {
  const [salaryStructure, setSalaryStructure] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { empid } = useParams();

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await api.get(`/salaryStructure/${empid}`);
        console.log("Fetched payslips:", response.data);
        setSalaryStructure(response.data);
      } catch (error) {
        console.error("Error fetching employees:", error);
        setSalaryStructure([]);
      }
    };

    fetchEmployees();
  }, []);

  const handleInputChange = (field, value) => {
    setSalaryStructure(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await api.put(`/salaryStructure/${empid}`, salaryStructure);
      toast.success("Salary structure updated successfully");
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating salary structure:", error);
      toast.error("Failed to update salary structure");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    // Refetch data to reset changes
    const fetchEmployees = async () => {
      try {
        const response = await api.get(`/salaryStructure/${empid}`);
        setSalaryStructure(response.data);
      } catch (error) {
        console.error("Error fetching employees:", error);
      }
    };
    fetchEmployees();
  };

  return (
    <div className="w-full h-full p-2 mb-16">
      <div className="mb-4 flex items-center justify-between bg-white rounded-2xl p-4 shadow">
        <h1 className="text-xl font-bold text-gray-800">Salary Structure</h1>
        <div className="flex gap-2">
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors"
            >
              Edit
            </button>
          ) : (
            <>
              <button
                onClick={handleCancel}
                className="px-4 py-2 text-sm bg-gray-500 text-white rounded-full hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="px-4 py-2 text-sm bg-green-600 text-white rounded-full hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {isSaving ? "Saving..." : "Save"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="bg-white shadow rounded-lg p-6 space-y-6">
        {/* Earnings Section */}
        <div>
          <h2 className="text-sm font-semibold text-gray-700 mb-3 pb-2 border-b">
            Earnings
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Basic Salary
              </label>
              <input
                type="number"
                value={salaryStructure.basic || ""}
                onChange={(e) => handleInputChange('basic', e.target.value)}
                className={`w-full px-3 py-2 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                  isEditing ? 'bg-white' : 'bg-gray-50'
                }`}
                readOnly={!isEditing}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                HRA
              </label>
              <input
                type="number"
                value={salaryStructure.hra || ""}
                onChange={(e) => handleInputChange('hra', e.target.value)}
                className={`w-full px-3 py-2 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                  isEditing ? 'bg-white' : 'bg-gray-50'
                }`}
                readOnly={!isEditing}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Special Allowance
              </label>
              <input
                type="number"
                value={salaryStructure.speacialAllowance || ""}
                onChange={(e) => handleInputChange('speacialAllowance', e.target.value)}
                className={`w-full px-3 py-2 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                  isEditing ? 'bg-white' : 'bg-gray-50'
                }`}
                readOnly={!isEditing}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Medical Allowance
              </label>
              <input
                type="number"
                value={salaryStructure.medicalAllowance || ""}
                onChange={(e) => handleInputChange('medicalAllowance', e.target.value)}
                className={`w-full px-3 py-2 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                  isEditing ? 'bg-white' : 'bg-gray-50'
                }`}
                readOnly={!isEditing}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Transport Allowance
              </label>
              <input
                type="number"
                value={salaryStructure.transportAllowance || ""}
                onChange={(e) => handleInputChange('transportAllowance', e.target.value)}
                className={`w-full px-3 py-2 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                  isEditing ? 'bg-white' : 'bg-gray-50'
                }`}
                readOnly={!isEditing}
              />
            </div>
          </div>
        </div>

        {/* Deductions Section */}
        <div>
          <h2 className="text-sm font-semibold text-gray-700 mb-3 pb-2 border-b">
            Deductions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Employee PF
              </label>
              <input
                type="number"
                value={salaryStructure.employeePf || ""}
                onChange={(e) => handleInputChange('employeePf', e.target.value)}
                className={`w-full px-3 py-2 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                  isEditing ? 'bg-white' : 'bg-gray-50'
                }`}
                readOnly={!isEditing}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Employer PF
              </label>
              <input
                type="number"
                value={salaryStructure.employerPf || ""}
                onChange={(e) => handleInputChange('employerPf', e.target.value)}
                className={`w-full px-3 py-2 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                  isEditing ? 'bg-white' : 'bg-gray-50'
                }`}
                readOnly={!isEditing}
              />
            </div>
          </div>
        </div>

        {/* Summary Section */}
        <div>
          <h2 className="text-sm font-semibold text-gray-700 mb-3 pb-2 border-b">
            Summary
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Gross Salary
              </label>
              <input
                type="text"
                value={salaryStructure.gross || ""}
                className="w-full px-3 py-2 text-sm border-2 border-green-200 rounded bg-green-50 font-semibold text-green-700 focus:outline-none"
                readOnly
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Cost to Company (CTC)
              </label>
              <input
                type="text"
                value={salaryStructure.ctc || ""}
                className="w-full px-3 py-2 text-sm border-2 border-blue-200 rounded bg-blue-50 font-semibold text-blue-700 focus:outline-none"
                readOnly
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SalaryStructure;